import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Wire schemas for `/admin/settings/admins/*` — admin-prd §12.2, Wave 2.54
 * BE-1 (TASK-293).
 *
 * Why a dedicated module (separate from `admin-invite.ts`):
 *   - `admin-invite.ts` already owns the `invite` + public `accept-invite`
 *     contracts. The list / patch surface introduced here is purely an
 *     internal staff CRUD on `internal.users` and never leaves the admin
 *     SPA boundary — keeping it isolated mirrors the project pattern
 *     (`account.ts`, `selector.ts`, etc).
 *   - The Wave 2.54 CEO decision locks invite + edit power to
 *     `admin OR super_admin` (was super-admin-only) while making
 *     `super_admin` itself immutable through this API. Surfacing the
 *     contract here makes the locked-down behaviour explicit on the wire.
 *
 * Role semantics (admin-prd §3.2 + decisions 2026-05-21):
 *   - `admin`        — internal staff with full mutation rights except
 *     creating other `super_admin`s.
 *   - `selector`     — content uploader; sees the catalog through their
 *     own lens.
 *   - `super_admin`  — break-glass role created only via the bootstrap
 *     CLI (TASK-294). The admins API NEVER promotes to / demotes from
 *     this role.
 */
/**
 * Subset of `users.role` that this API surface lists / edits. Tenant roles
 * (`client_owner`, `client_manager`) are out of scope — they live behind
 * the per-account team API (`/admin/accounts/:id/team/*`).
 */
export const adminUserRoleSchema = z.enum(['admin', 'selector', 'super_admin']);
/**
 * Roles assignable via `PATCH /admin/settings/admins/:user_id`. Excludes
 * `super_admin` by construction — Zod rejects the promotion attempt with a
 * `VALIDATION_ERROR` before any business rule runs.
 */
export const adminPatchAssignableRoleSchema = z.enum(['admin', 'selector']);
// ---------------------------------------------------------------------------
// List filter / pagination (admin-arch §3.0.1 keyset cursor).
// Mirrors `selectorFilterSchema` shape for FE re-use.
// ---------------------------------------------------------------------------
const Q_MAX = 200;
const CURSOR_MAX = 200;
export const adminUserFilterSchema = z.object({
    role: adminUserRoleSchema.optional(),
    is_active: z
        .union([z.literal('true'), z.literal('false'), z.boolean()])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
    q: z.string().min(1).max(Q_MAX).optional(),
    limit: z.coerce.number().int().positive().max(100).default(50),
    cursor: z.string().max(CURSOR_MAX).optional(),
});
// ---------------------------------------------------------------------------
// List item (admin-prd §12.2 "Пользователи" page).
// ---------------------------------------------------------------------------
export const adminUserListItemSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    name: z.string(),
    role: adminUserRoleSchema,
    is_active: z.boolean(),
    is_super_admin: z.boolean(),
    // `totp_enabled` is the canonical 2FA-enrolment signal — sourced from
    // `internal.admin_2fa.totp_enabled` (Wave 2.57 BE-2, TASK-297-followup).
    // `false` when no `admin_2fa` row exists for the user.
    totp_enabled: z.boolean(),
    last_login_at: isoDateTimeSchema.nullable(),
    created_at: isoDateTimeSchema,
});
export const adminUserListResponseSchema = z.object({
    items: z.array(adminUserListItemSchema),
    next_cursor: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Patch request (Wave 2.54 BE-1). One non-empty field minimum so a no-op
// request fails fast instead of writing a useless audit row.
// ---------------------------------------------------------------------------
export const adminUserPatchRequestSchema = z
    .object({
    role: adminPatchAssignableRoleSchema.optional(),
    is_active: z.boolean().optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field (role, is_active) must be provided');
/**
 * `PATCH /admin/settings/admins/:user_id` response — refreshed list item so
 * the FE can update its table row without a refetch.
 */
export const adminUserPatchResponseSchema = adminUserListItemSchema;
// ---------------------------------------------------------------------------
// Error codes — single source of truth for the FE error-mapping table.
//
// `SUPER_ADMIN_INVITE_FORBIDDEN` is raised by `POST .../invite` (Zod layer
// rejects `super_admin` before the service runs — the `adminInviteRoleEnum`
// in `admin-invite.ts` already excludes the role; the server then surfaces
// the failure with this code for FE differentiation).
//
// `SUPER_ADMIN_IMMUTABLE` / `CANNOT_EDIT_SELF` are raised by the PATCH
// endpoint guard branches.
// ---------------------------------------------------------------------------
export const adminUserErrorCodeSchema = z.enum([
    'SUPER_ADMIN_IMMUTABLE',
    'SUPER_ADMIN_INVITE_FORBIDDEN',
    'CANNOT_EDIT_SELF',
    'USER_NOT_FOUND',
    'INVALID_CURSOR',
]);
// ---------------------------------------------------------------------------
// BUG-082 — admin-level force-logout (POST /admin/settings/admins/:user_id/force-logout).
//
// Revokes every non-revoked `internal.user_sessions` row for the target admin
// in a single transaction. Idempotent: a target with no active sessions returns
// `revoked_sessions: 0`. Audit event `admin.session_force_logout` is emitted
// once per call with the count + actor metadata.
// ---------------------------------------------------------------------------
export const adminForceLogoutRequestSchema = z
    .object({
    reason: z.string().min(1).max(500).optional(),
})
    .strict();
export const adminForceLogoutResponseSchema = z.object({
    user_id: uuidSchema,
    revoked_sessions: z.number().int().nonnegative(),
});
//# sourceMappingURL=admins.js.map